import { format, isValid, parseISO } from "date-fns";
import { resolveScheduleStatusName } from "./scheduleStatuses";
import { scheduleIdText, formatTimeCell, formatCreatedByDisplay } from "./scheduleHelpers";

export const INITIAL_SCHEDULE_DIRECTORY_FILTERS = {
  start_date: "",
  end_date: "",
  schedule_id: "",
  exact_schedule_date: "",
  schedule_title: "",
  schedule_time: "",
  schedule_type_name: "",
  assigned_to_name: "",
  priority_name: "",
  status: "",
  created_by_name: "",
  page: 1,
};

export function scheduleDateKey(row) {
  const v = row?.schedule_date;
  if (!v) return "";
  return String(v).slice(0, 10);
}

export function formatDateDisplay(value) {
  if (!value) return "";
  try {
    const d = parseISO(String(value).slice(0, 10));
    return isValid(d) ? format(d, "dd MMM yyyy") : String(value).slice(0, 10);
  } catch {
    return String(value).slice(0, 10);
  }
}

export function padTime(value) {
  return formatTimeCell(value) === "—" ? "" : formatTimeCell(value);
}

function inDateRange(row, start, end) {
  const key = scheduleDateKey(row);
  if (!key) return false;
  if (start && key < start) return false;
  if (end && key > end) return false;
  return true;
}

export function applyScheduleDirectoryFilters(rows, filters) {
  return (rows || []).filter((row) => {
    if (filters.start_date || filters.end_date) {
      if (!inDateRange(row, filters.start_date, filters.end_date)) return false;
    }
    if (filters.schedule_id) {
      const needle = String(filters.schedule_id).toLowerCase();
      if (!scheduleIdText(row).toLowerCase().includes(needle)) return false;
    }
    if (filters.exact_schedule_date && scheduleDateKey(row) !== filters.exact_schedule_date) {
      return false;
    }
    if (filters.schedule_title) {
      const n = String(row.schedule_title || "").toLowerCase();
      if (!n.includes(String(filters.schedule_title).toLowerCase())) return false;
    }
    if (filters.schedule_time && padTime(row.schedule_time) !== padTime(filters.schedule_time)) {
      return false;
    }
    if (filters.schedule_type_name) {
      if (
        String(row.schedule_type_name || "").toLowerCase() !==
        String(filters.schedule_type_name).toLowerCase()
      ) {
        return false;
      }
    }
    if (filters.assigned_to_name) {
      if (
        String(row.assigned_to_name || "").toLowerCase() !==
        String(filters.assigned_to_name).toLowerCase()
      ) {
        return false;
      }
    }
    if (filters.priority_name) {
      if (
        String(row.priority_name || "").toLowerCase() !==
        String(filters.priority_name).toLowerCase()
      ) {
        return false;
      }
    }
    if (filters.status) {
      if (resolveScheduleStatusName(row).toLowerCase() !== String(filters.status).toLowerCase()) {
        return false;
      }
    }
    if (filters.created_by_name) {
      const needle = String(filters.created_by_name).toLowerCase();
      const name = String(row.created_by_name || "").toLowerCase();
      if (!name.includes(needle)) return false;
    }
    return true;
  });
}

export function hasActiveScheduleDirectoryFilters(filters) {
  if (!filters) return false;
  return Boolean(
    filters.start_date ||
      filters.end_date ||
      filters.schedule_id ||
      filters.exact_schedule_date ||
      filters.schedule_title ||
      filters.schedule_time ||
      filters.schedule_type_name ||
      filters.assigned_to_name ||
      filters.priority_name ||
      filters.status ||
      filters.created_by_name,
  );
}

export function buildScheduleExportRows(rows, visibleColumns) {
  const cols = (visibleColumns || []).filter((c) => c.id !== "actions");
  return (rows || []).map((row) => {
    const out = {};
    for (const col of cols) {
      switch (col.id) {
        case "schedule_id":
          out[col.label] = scheduleIdText(row);
          break;
        case "schedule_date":
          out[col.label] = formatDateDisplay(row.schedule_date);
          break;
        case "schedule_time":
          out[col.label] = padTime(row.schedule_time);
          break;
        case "status":
          out[col.label] = resolveScheduleStatusName(row) || "—";
          break;
        case "created_by_name":
          out[col.label] = formatCreatedByDisplay(row);
          break;
        default:
          out[col.label] = row[col.id] ?? "—";
      }
    }
    return out;
  });
}
